import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getUserOrders } from '../api/apiendpoint';
import { motion, AnimatePresence } from 'framer-motion';
import Accordion from 'react-bootstrap/Accordion';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [retryCount, setRetryCount] = useState(0);

  // Memoized payment methods for filter options
  const paymentMethods = useMemo(() => {
    const methods = new Set(orders.map(order => order.payment_method));
    return Array.from(methods).sort();
  }, [orders]);

  // Load orders with retry logic
  const loadOrders = useCallback(async (isRetry = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getUserOrders();
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format');
      }
      
      setOrders(data);
      setRetryCount(0);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
      
      // Auto-retry logic (max 3 attempts)
      if (!isRetry && retryCount < 3) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadOrders(true);
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Filter and sort orders
  const processedOrders = useMemo(() => {
    let filtered = orders;

    // Apply filters
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(order => order.payment_method === paymentFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount-high':
          return parseFloat(b.subtotal) - parseFloat(a.subtotal);
        case 'amount-low':
          return parseFloat(a.subtotal) - parseFloat(b.subtotal);
        default:
          return 0;
      }
    });

    return filtered;
  }, [orders, paymentFilter, statusFilter, sortBy]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'danger';
      case 'processing':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Get status display text
  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Awaiting Processing';
      case 'processing':
        return 'Being Processed';
      case 'completed':
        return 'Completed';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="mt-3 text-muted">
              Loading your orders...
              {retryCount > 0 && ` (Attempt ${retryCount + 1})`}
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger" className="text-center">
              <Alert.Heading>Unable to Load Orders</Alert.Heading>
              <p className="mb-3">{error}</p>
              <Button variant="outline-danger" onClick={() => loadOrders()}>
                Try Again
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Container className="mt-5">
        <Row className="mb-4">
          <Col>
            <h2 className="mb-3 fw-bold">My Orders</h2>
            <p className="text-muted">View and manage your order history</p>
          </Col>
        </Row>

        {/* Filters and Controls */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Payment Method</Form.Label>
                  <Form.Select
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                  >
                    <option value="all">All Methods</option>
                    {paymentMethods.map(method => (
                      <option key={method} value={method}>
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Sort By</Form.Label>
                  <Form.Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="amount-high">Amount: High to Low</option>
                    <option value="amount-low">Amount: Low to High</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button
                  variant="outline-primary"
                  onClick={() => loadOrders()}
                  className="w-100"
                >
                  Refresh
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Orders List */}
        <AnimatePresence>
          {processedOrders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Alert variant="info" className="text-center py-5">
                <h5>No Orders Found</h5>
                <p className="mb-0">
                  {paymentFilter !== 'all' || statusFilter !== 'all'
                    ? 'No orders match your current filters. Try adjusting your search criteria.'
                    : 'You haven\'t placed any orders yet.'}
                </p>
              </Alert>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="mb-3">
                <small className="text-muted">
                  Showing {processedOrders.length} of {orders.length} orders
                </small>
              </div>

              <Accordion>
                {processedOrders.map((order, idx) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Accordion.Item eventKey={String(idx)} className="mb-3 shadow-sm">
                      <Accordion.Header>
                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                          <div>
                            <h6 className="mb-1 fw-bold">Order #{order.id}</h6>
                            <small className="text-muted">
                              {formatDate(order.created_at)}
                            </small>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge bg={getStatusBadgeVariant(order.status)}>
                              {getStatusText(order.status)}
                            </Badge>
                            <span className="fw-bold text-primary">
                              {formatCurrency(order.subtotal)}
                            </span>
                          </div>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Card className="border-0">
                          <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <h5 className="mb-0 fw-bold">Order Details</h5>
                              <small className="text-muted">Order #{order.id}</small>
                            </div>

                            {/* Customer Information */}
                            <Row className="mb-4">
                              <Col md={6}>
                                <h6 className="text-muted mb-3">Customer Information</h6>
                                <div className="mb-2">
                                  <strong>Name:</strong> {order.customer_name}
                                </div>
                                <div className="mb-2">
                                  <strong>Email:</strong> {order.email}
                                </div>
                                <div className="mb-2">
                                  <strong>Address:</strong> {order.address}
                                </div>
                                {order.company_name && (
                                  <div className="mb-2">
                                    <strong>Company:</strong> {order.company_name}
                                  </div>
                                )}
                              </Col>
                              <Col md={6}>
                                <h6 className="text-muted mb-3">Order Information</h6>
                                <div className="mb-2">
                                  <strong>Order Date:</strong> {formatDate(order.created_at)}
                                </div>
                                <div className="mb-2">
                                  <strong>Payment Method:</strong>{' '}
                                  <span className="text-capitalize">{order.payment_method}</span>
                                </div>
                                <div className="mb-2">
                                  <strong>Status:</strong>{' '}
                                  <Badge bg={getStatusBadgeVariant(order.status)}>
                                    {getStatusText(order.status)}
                                  </Badge>
                                </div>
                              </Col>
                            </Row>

                            {/* Order Items */}
                            <h6 className="text-muted mb-3">Order Items</h6>
                            <div className="border rounded p-3 mb-4">
                              {order.items?.map((item, i) => (
                                <div key={i} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                  <div>
                                    <div className="fw-semibold">{item.name}</div>
                                    <small className="text-muted">Quantity: {item.quantity}</small>
                                  </div>
                                  <div className="text-end">
                                    <div className="fw-semibold">
                                      {formatCurrency(item.quantity * item.price)}
                                    </div>
                                    <small className="text-muted">
                                      {formatCurrency(item.price)} each
                                    </small>
                                  </div>
                                </div>
                              )) || (
                                <div className="text-center text-muted py-3">
                                  No items available
                                </div>
                              )}
                            </div>

                            {/* Order Summary */}
                            <div className="border rounded p-3 bg-light">
                              <Row className="mb-2">
                                <Col>Subtotal:</Col>
                                <Col className="text-end">
                                  {formatCurrency(
                                    order.items?.reduce((sum, item) => sum + (item.quantity * item.price), 0) || 0
                                  )}
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col>Shipping:</Col>
                                <Col className="text-end">
                                  {formatCurrency(order.shipping_cost || 0)}
                                </Col>
                              </Row>
                              <Row className="fw-bold fs-5 pt-2 border-top">
                                <Col>Total:</Col>
                                <Col className="text-end text-primary">
                                  {formatCurrency(order.subtotal)}
                                </Col>
                              </Row>
                            </div>

                            {/* Support Contact */}
                            <div className="mt-4 pt-3 border-top text-center">
                              <p className="mb-0 text-muted">
                                Need help with your order?{' '}
                                <a href="#!" className="text-decoration-none">
                                  Contact our support team
                                </a>
                              </p>
                            </div>
                          </Card.Body>
                        </Card>
                      </Accordion.Body>
                    </Accordion.Item>
                  </motion.div>
                ))}
              </Accordion>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </motion.div>
  );
};

export default MyOrders;